import React, { useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { expValEquals } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { View, ViewAs } from '@atlassian/jira-software-view-settings/src/common/types/constant.tsx';
import ViewSettings from '@atlassian/jira-software-view-settings/src/ui/index.tsx';
import type { CardEntryGroup } from '../../../model/types.tsx';
import {
	collapseAllCardGroups,
	setExpandedCardGroups,
} from '../../../state/actions/card-list/index.tsx';
import { useBacklogActionCreator, useBacklogSelector } from '../../../state/index.tsx';
import { getIsCompanyManaged, getRapidViewId } from '../../../state/selectors/app/index.tsx';
import {
	backlogHasStoryPoints,
	backlogHasEstimates,
	getCardExtraFields,
	backlogHasDueDate,
	backlogHasPriority,
} from '../../../state/selectors/cards/index.tsx';
import {
	getCanInviteOthersToProject,
	getConfig,
	getShowEpicAsPanel,
} from '../../../state/selectors/config/index.tsx';
import { getCMPFilters } from '../../../state/selectors/filters/index.tsx';
import { canEditBoardConfig } from '../../../state/selectors/permissions/index.tsx';
import { isVersionsFeatureEnabled } from '../../../state/selectors/version/index.tsx';
import { useAllCardGroups } from '../../../state/state-hooks/use-all-card-groups.tsx';

type Props = {
	isPanelOpen: boolean;
	onPanelClose: () => void;
};

export const ViewSettingsPanel = ({ isPanelOpen, onPanelClose }: Props) => {
	const boardId = useBacklogSelector(getRapidViewId);
	const isCMPBoard = useBacklogSelector(getIsCompanyManaged);
	const showEpicAsPanel = useBacklogSelector(getShowEpicAsPanel);
	const allCardGroups: CardEntryGroup[] = useAllCardGroups();

	const isQuickFiltersAvailable = useBacklogSelector(getCMPFilters).length > 0;
	const isVersionsEnabled = useBacklogSelector(isVersionsFeatureEnabled);
	const showFilterBarToggle = isCMPBoard && isQuickFiltersAvailable;

	const config = useBacklogSelector(getConfig);
	const epicCustomName = config.hierarchyNomenclature?.firstLevelName;
	const canEdit = useBacklogSelector(canEditBoardConfig);
	const canInvite = useBacklogSelector(getCanInviteOthersToProject);

	const shouldShowSubtasks: boolean = useMemo(() => !isEmpty(allCardGroups), [allCardGroups]);

	const areAllSubtasksExpanded: boolean = useMemo(
		() => allCardGroups.every((card) => !card.isCollapsed),
		[allCardGroups],
	);

	const areAllSubtasksCollapsed: boolean = useMemo(
		() => allCardGroups.every((card) => card.isCollapsed),
		[allCardGroups],
	);

	const handleExpandSubtasks = useBacklogActionCreator(() =>
		setExpandedCardGroups(allCardGroups.map((card) => card.parentId.toString())),
	);

	const handleCollapseSubtasks = useBacklogActionCreator(() => collapseAllCardGroups());

	const cardExtraFields = useBacklogSelector(getCardExtraFields);
	const hasCardsWithStoryPoint = useBacklogSelector(backlogHasStoryPoints);
	const hasCardsWithEstimate = useBacklogSelector(backlogHasEstimates);

	const isDueDateEnabled = useBacklogSelector(backlogHasDueDate);
	const isPriorityEnabled = useBacklogSelector(backlogHasPriority);

	return (
		<ViewSettings
			view={View.BACKLOG}
			viewAs={ViewAs.PANEL}
			isViewPanelOpen={isPanelOpen}
			onViewPanelClose={onPanelClose}
			boardId={Number(boardId)}
			isClassic={isCMPBoard}
			areSubtasksEnabled={isCMPBoard && shouldShowSubtasks}
			onSubtasksExpandAll={handleExpandSubtasks}
			onSubtasksCollapseAll={handleCollapseSubtasks}
			areAllSubtasksExpanded={areAllSubtasksExpanded}
			areAllSubtasksCollapsed={areAllSubtasksCollapsed}
			showEpicAsPanel={showEpicAsPanel}
			epicCustomName={epicCustomName}
			isVersionsEnabled={isVersionsEnabled}
			showFilterBarToggle={showFilterBarToggle}
			cardExtraFields={cardExtraFields}
			isEstimateEnabled={hasCardsWithStoryPoint || hasCardsWithEstimate}
			isDueDateEnabled={
				fg('jsw_cmp_show_due_date_by_default') ? isDueDateEnabled : !isCMPBoard && isDueDateEnabled
			}
			isPriorityEnabled={isPriorityEnabled}
			isAddPeopleButtonEnabled={
				canEdit ||
				(canInvite && expValEquals('open_invite_for_open_tmp_projects', 'cohort', 'experiment'))
			}
		/>
	);
};
